import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchProperties, 
  fetchPropertyById, 
  createProperty, 
  updateProperty,
  fetchRevenueManagerBranchList,
  PropertiesResponse,
  RevenueManagerBranchListResponse
} from '@/api/properties';
import { Property } from '@/types/revenue-manager';
import { useAuth } from '@/contexts/AuthContext';

export function useProperties() {
  return useQuery<PropertiesResponse>({
    queryKey: ['properties'],
    queryFn: fetchProperties,
  });
}

export function useProperty(propertyId: string) {
  return useQuery({
    queryKey: ['properties', propertyId],
    queryFn: () => fetchPropertyById(propertyId),
    enabled: !!propertyId,
  });
}

export function useCreateProperty() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (property: Omit<Property, 'id'>) => createProperty(property),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

export function useUpdateProperty() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ propertyId, updates }: { propertyId: string; updates: Partial<Property> }) => 
      updateProperty(propertyId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['properties', variables.propertyId] });
    },
  });
}

/**
 * Hook to fetch revenue manager branch list
 */
export function useRevenueManagerBranches() {
  const { canMakeApiCalls } = useAuth();

  return useQuery<RevenueManagerBranchListResponse>({
    queryKey: ['revenue-manager-branches'],
    queryFn: fetchRevenueManagerBranchList,
    enabled: canMakeApiCalls,
  });
}
